import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';

/**
 * Service de gestion des questionnaires d'évaluation des bénéficiaires
 */

/**
 * Initialise un nouveau questionnaire d'évaluation vierge
 */
export const initializeAssessment = async (db, beneficiaryId) => {
  try {
    const clientRef = doc(db, 'clients', beneficiaryId);

    const emptyAssessment = {
      status: 'non_commencé',
      startedAt: serverTimestamp(),
      lastUpdatedAt: serverTimestamp(),
      completedAt: null,
      completedBy: null,

      prealables: {
        evaluationDate: null,
        evaluationDuration: '',
      },

      familialStatus: {
        maritalStatus: '',
        livingWith: [],
        numberOfChildren: null,
      },

      legalProtection: {
        hasProtection: false,
        protectionType: '',
        representative: {
          name: '',
          organization: '',
          phone: '',
          email: '',
        }
      },

      contacts: {
        trustedPerson: {
          exists: false,
          name: '',
          relationship: '',
          phone: '',
          email: '',
        },
        emergencyContact: {
          name: '',
          relationship: '',
          phone1: '',
          phone2: '',
        },
        familySupport: {
          hasCloseFamily: false,
          familyMembers: '',
          contactFrequency: '',
          supportLevel: '',
        },
        socialNetwork: {
          hasFriends: false,
          frequency: '',
        }
      },

      housing: {
        type: '',
        status: '',
        floor: null,
        hasElevator: false,
        numberOfRooms: null,
        access: '',
        adaptations: [],
        proximity: {
          shops: '',
          publicTransport: '',
          doctor: '',
        },
        safety: {
          hasRisks: false,
          risksDescription: '',
          electrical: '',
          heating: {
            type: '',
            status: '',
            issues: '',
          }
        },
        pets: {
          hasPets: false,
          type: '',
          behavior: '',
        }
      },

      health: {
        generalHealth: '',
        mainPathologies: '',
        lastCheckup: null,
        conditions: [],
        medications: {
          takesRegularly: false,
          count: null,
          needsHelp: false,
          hasPillBox: false,
          preparedBy: '',
        },
        professionals: {
          doctor: { name: '', phone: '', frequency: '' },
          nurse: { has: false, frequency: '' },
          physiotherapist: { has: false, frequency: '' },
          others: '',
        },
        hospitalizations: {
          last12Months: false,
          count: null,
          reasons: '',
          planned: { has: false, date: null, reason: '' },
        },
        mobility: {
          indoor: '',
          fallRisk: '',
          fallsLast6Months: null,
          canGoOutAlone: '',
          transportation: [],
        }
      },

      autonomy: {
        hygiene: {
          completeWash: '',
          faceHands: '',
          showerBath: '',
          dressingUpper: '',
          dressingLower: '',
          shoes: '',
          hair: '',
          shavingMakeup: '',
          nails: '',
        },
        elimination: {
          toilet: '',
          urinaryContinence: '',
          fecalContinence: '',
          protections: { uses: false, type: '' },
        },
        nutrition: {
          prepareMeal: '',
          reheatMeal: '',
          eatAlone: '',
          cutFood: '',
          drinkAlone: '',
          specialDiet: { has: false, type: '' },
          appetite: '',
          weight: null,
          height: null,
          weightChange: { status: '', amount: null },
        },
        mobility: {
          getUpFromBed: '',
          lieDown: '',
          getUpFromChair: '',
          stairs: '',
          moveIndoor: '',
          leaveHome: '',
        },
        housekeeping: {
          dailyTidying: '',
          dusting: '',
          vacuuming: '',
          washFloors: '',
          dishes: '',
          windows: '',
          laundry: '',
          hangFoldLaundry: '',
          ironing: '',
          changeSheets: '',
        },
        domestic: {
          shopping: '',
          carryBags: '',
          putAwayGroceries: '',
          manageMail: '',
          usePhone: '',
          manageBudget: '',
          administrativeTasks: '',
        },
        cognitive: {
          memory: '',
          temporalOrientation: '',
          spatialOrientation: '',
          understanding: '',
          expression: '',
          vision: '',
          hearing: '',
        }
      },

      psychological: {
        mood: '',
        feelings: [],
        hasFollowUp: false,
        followUpProvider: '',
        takesMedication: false,
        medications: '',
        activities: [],
        activityFrequency: '',
        accompanimentInterest: [],
        socialLife: {
          visitFrequency: '',
          participatesInActivities: false,
        }
      },

      currentAids: {
        homeHelp: {
          has: false,
          organization: '',
          frequency: '',
          weeklyHours: null,
        },
        mealDelivery: { has: false, frequency: '' },
        teleassistance: { has: false, organization: '' },
        otherServices: '',
        financialAids: {
          apa: { has: false, gir: '', amount: null },
          pch: { has: false, amount: null },
          retirement: { has: false, details: '' },
          department: false,
          other: '',
        },
        needsAssistanceWithApplications: false,
      },

      needs: {
        services: {
          personalCare: [],
          housekeeping: [],
          accompaniment: [],
          stimulation: [],
          other: [],
          additionalNeeds: '',
        },
        schedule: {
          frequency: '',
          daysOfWeek: [],
          timeSlots: [],
          durationPerVisit: '',
          weeklyHours: null,
        },
        caregiverProfile: {
          genderPreference: '',
          languages: [],
          qualifications: '',
          criteria: [],
        },
        expectations: '',
        importantInfo: '',
      },

      project: {
        objectives: [],
        mainPriority: '',
        caregiver: {
          hasCaregiver: false,
          identity: '',
          relationship: '',
          frequency: '',
          burden: '',
          needsRespite: false,
          support: [],
        }
      },

      observations: {
        evaluatorNotes: '',
        suggestedInterventions: '',
        urgentNeeds: '',
        additionalComments: '',
      }
    };

    await updateDoc(clientRef, {
      assessmentQuestionnaire: emptyAssessment,
      updatedAt: serverTimestamp()
    });

    return emptyAssessment;
  } catch (error) {
    console.error('Erreur lors de l\'initialisation du questionnaire:', error);
    throw error;
  }
};

/**
 * Sauvegarde une section spécifique du questionnaire
 */
export const saveAssessmentSection = async (db, beneficiaryId, sectionName, sectionData) => {
  try {
    const clientRef = doc(db, 'clients', beneficiaryId);

    const updatePath = `assessmentQuestionnaire.${sectionName}`;
    const updates = {
      [updatePath]: sectionData,
      'assessmentQuestionnaire.status': 'en_cours',
      'assessmentQuestionnaire.lastUpdatedAt': serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    await updateDoc(clientRef, updates);

    console.log(`Section ${sectionName} sauvegardée avec succès`);
  } catch (error) {
    console.error(`Erreur lors de la sauvegarde de la section ${sectionName}:`, error);
    throw error;
  }
};

/**
 * Récupère le questionnaire d'évaluation complet
 */
export const getAssessment = async (db, beneficiaryId) => {
  try {
    const clientRef = doc(db, 'clients', beneficiaryId);
    const clientSnap = await getDoc(clientRef);

    if (clientSnap.exists()) {
      const data = clientSnap.data();
      return data.assessmentQuestionnaire || null;
    }

    return null;
  } catch (error) {
    console.error('Erreur lors de la récupération du questionnaire:', error);
    throw error;
  }
};

/**
 * Marque le questionnaire comme complété
 */
export const completeAssessment = async (db, beneficiaryId, completedBy) => {
  try {
    const clientRef = doc(db, 'clients', beneficiaryId);

    // Nettoyer l'objet completedBy pour éviter les champs undefined
    const cleanCompletedBy = {};
    if (completedBy) {
      if (completedBy.uid) cleanCompletedBy.uid = completedBy.uid;
      if (completedBy.displayName) cleanCompletedBy.displayName = completedBy.displayName;
      if (completedBy.email) cleanCompletedBy.email = completedBy.email;
      if (completedBy.role) cleanCompletedBy.role = completedBy.role;
    }

    await updateDoc(clientRef, {
      'assessmentQuestionnaire.status': 'complété',
      'assessmentQuestionnaire.completedAt': serverTimestamp(),
      'assessmentQuestionnaire.completedBy': cleanCompletedBy,
      'assessmentQuestionnaire.lastUpdatedAt': serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    console.log('Questionnaire marqué comme complété');
  } catch (error) {
    console.error('Erreur lors de la finalisation du questionnaire:', error);
    throw error;
  }
};

/**
 * Réinitialise le questionnaire (admin uniquement)
 */
export const resetAssessment = async (db, beneficiaryId) => {
  try {
    const clientRef = doc(db, 'clients', beneficiaryId);

    await updateDoc(clientRef, {
      assessmentQuestionnaire: null,
      updatedAt: serverTimestamp()
    });

    console.log('Questionnaire réinitialisé');
  } catch (error) {
    console.error('Erreur lors de la réinitialisation du questionnaire:', error);
    throw error;
  }
};

/**
 * Obtient le statut du questionnaire
 */
export const getAssessmentStatus = async (db, beneficiaryId) => {
  try {
    const assessment = await getAssessment(db, beneficiaryId);

    if (!assessment) {
      return 'non_commencé';
    }

    return assessment.status || 'non_commencé';
  } catch (error) {
    console.error('Erreur lors de la récupération du statut:', error);
    return 'non_commencé';
  }
};

/**
 * Calcule le pourcentage de complétion du questionnaire
 */
export const calculateCompletionPercentage = (assessment) => {
  if (!assessment) return 0;

  const sections = [
    'prealables', 'familialStatus', 'legalProtection', 'contacts',
    'housing', 'health', 'autonomy', 'psychological',
    'currentAids', 'needs', 'project', 'observations'
  ];

  let filledSections = 0;

  sections.forEach(section => {
    if (assessment[section]) {
      const sectionData = assessment[section];
      const hasContent = Object.values(sectionData).some(value => {
        if (typeof value === 'object' && value !== null) {
          return Object.values(value).some(v => v !== '' && v !== null && v !== false && (Array.isArray(v) ? v.length > 0 : true));
        }
        return value !== '' && value !== null && value !== false && (Array.isArray(value) ? value.length > 0 : true);
      });

      if (hasContent) {
        filledSections++;
      }
    }
  });

  return Math.round((filledSections / sections.length) * 100);
};
